import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Grid,
  Button,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import {
  GridColumns,
  GridCellParams,
  getGridNumericColumnOperators,
} from '@material-ui/data-grid';

import PaginatedDataGrid from 'components/PaginatedDataGrid';
import CollapsibleList from 'components/CollapsibleList';
import { FORM_MODE } from 'shared/enums';
import ActionsMenu from './ActionsMenu';
import { RootState } from 'store';
import { REQUEST_LIFE_CICLE } from 'interfaces/requests';

import { listMailingRulesAction } from 'modules/mailingRuleList';
import { setSelectedMailingRuleModeAction } from 'modules/mailingRuleModeNavigation';

import { useStyles } from './styles';

import StatusFilterHeader from './statusFilterHeader';
import { isLoading } from 'utils/loading';
import { parseDateTimeToPTBR } from 'utils/date';

const MailingRuleList: React.FC = () => {
  const dispatch = useDispatch();
  const classes = useStyles();

  // useEffect(() => {
  //   dispatch(listMailingRulesAction({}));
  // }, [dispatch]);

  const { list, count, loading: mailingLoading } = useSelector(
    (state: RootState) => state.mailingRuleList,
  );

  const { loading: navigationLoading } = useSelector(
    (state: RootState) => state.selectedMailingRuleModeNavigation,
  );

  const { permissions, loading: userLoading } = useSelector(
    (state: RootState) => state.user,
  );

  const {
    list: { mailingRuleStatusEnums },
    loading: parametersLoading,
  } = useSelector((state: RootState) => state.parameters);

  const loading = isLoading([mailingLoading, userLoading, parametersLoading]);

  const handleCreateMailingRule = () => {
    dispatch(setSelectedMailingRuleModeAction(FORM_MODE.create));
  };

  const collapsibleList = (params: GridCellParams) => (
    <>
      <CollapsibleList
        items={params.value as { [key: string]: string }[]}
        idPropName="id"
        valuePropName="companyName"
      />
    </>
  );

  const getCustomerType = (params: GridCellParams) => {
    const items = (params.value as { [key: string]: string }[]) || [];

    const customerTypeRule = items.find(
      (item) => item.fact === 'CUSTOMER_TYPE',
    );
    if (!customerTypeRule) return '';

    const conditions = (customerTypeRule.conditions as unknown) as {
      [key: string]: string;
    }[];

    const customerTypes = conditions.map((item) => item.value);

    return customerTypes.join(' / ');
  };

  const getPermanence = (params: GridCellParams) => {
    const row = params.row as { [key: string]: string }[];

    if (row['permanenceInDays'])
      return (
        row['permanenceInDays'] +
        (row['permanenceInDays'] > 1 ? ' dias' : ' dia')
      );

    if (row['permanenceUntilDate'])
      return (
        'Até ' + parseDateTimeToPTBR(row['permanenceUntilDate'].toString())
      );

    return '';
  };

  const getUserName = (params: GridCellParams) => {
    const items = (params.value as { [key: string]: string }[]) || [];

    const createAction = items.find(
      (item) => item.historyAction === 'mailing-rule:create',
    );
    if (!createAction) return '';

    const user = (createAction.user as unknown) as { [key: string]: string };

    return user?.user || '';
  };

  const parsedAggregrationType = {
    CONTRACT: 'Nível Contrato',
    CUSTOMER: 'Nível Cliente',
  };

  const actionColumn: GridColumns =
    permissions['mailing-rule:show'] ||
    permissions['mailing-rule:inactivate'] ||
    permissions['mailing-rule:update']
      ? [
          {
            field: 'actions',
            headerName: 'Ações',
            width: 115,
            filterable: false,
            sortable: false,
            renderCell: (params: GridCellParams) =>
              ActionsMenu(params, {
                inactivateAction: permissions['mailing-rule:inactivate'],
                showAction: permissions['mailing-rule:show'],
                updateAction: permissions['mailing-rule:update'],
              }),
          },
        ]
      : [];

  const columns: GridColumns = [
    ...actionColumn,
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      valueGetter: (params: GridCellParams) =>
        params.value && mailingRuleStatusEnums[params.value.toString()],
      filterOperators: getGridNumericColumnOperators()
        .filter((operator) => operator.value === '=')
        .map((operator) => ({
          ...operator,
          InputComponent: StatusFilterHeader,
        })),
    },
    {
      field: 'name',
      headerName: 'Nome da Régua',
      width: 300,
      filterable: false,
      sortComparator: (v1, v2) => {
        const name1 = v1.toLowerCase() || '';
        const name2 = v2.toLowerCase() || '';
        return name1.localeCompare(name2);
      },
    },
    {
      field: 'collectRuleStrategies',
      headerName: 'EPSs',
      width: 400,
      filterable: false,
      sortComparator: (v1, v2) => {
        const companyName1 = v1[0]?.companyName?.toLowerCase() || '';
        const companyName2 = v2[0]?.companyName?.toLowerCase() || '';
        return companyName1.localeCompare(companyName2);
      },
      renderCell: (params: GridCellParams) => collapsibleList(params),
    },
    {
      field: 'serviceCategory',
      headerName: 'Tipo de atuação',
      width: 220,
      filterable: false,
      renderCell: (params: GridCellParams) => params.value['description'] || '',
      sortComparator: (v1, v2) => {
        const description1 = v1?.description?.toLowerCase() || '';
        const description2 = v2?.description?.toLowerCase() || '';
        return description1.localeCompare(description2);
      },
    },
    {
      field: 'aggregationType',
      headerName: 'Chave de distribuição',
      width: 210,
      renderCell: (params: GridCellParams) =>
        parsedAggregrationType[`${params.value}`],
      sortComparator: (v1, v2) => {
        const aggregationType1 =
          parsedAggregrationType[v1]?.toLowerCase() || '';
        const aggregationType2 =
          parsedAggregrationType[v2]?.toLowerCase() || '';
        return aggregationType1.localeCompare(aggregationType2);
      },
    },
    {
      field: 'rules',
      headerName: 'Tipo de pessoa',
      width: 200,
      filterable: false,
      renderCell: (params: GridCellParams) => getCustomerType(params),
      sortComparator: (v1, v2) => {
        const customerType1 =
          v1.find((item) => item.fact === 'CUSTOMER_TYPE')?.conditions[0]
            ?.value || '';
        const customerType2 =
          v2.find((item) => item.fact === 'CUSTOMER_TYPE')?.conditions[0]
            ?.value || '';
        return customerType1.localeCompare(customerType2);
      },
    },
    {
      field: 'permanenceInDays',
      headerName: 'Tempo de permanência',
      width: 220,
      filterable: false,
      renderCell: (params: GridCellParams) => getPermanence(params),
    },
    {
      field: 'createdAt',
      headerName: 'Data de criação',
      valueGetter: (params) =>
        params.value ? parseDateTimeToPTBR(params.value.toString()) : '',
      width: 170,
      filterable: false,
    },
    {
      field: 'history',
      headerName: 'Usuário',
      width: 150,
      filterable: false,
      renderCell: (params: GridCellParams) => getUserName(params),
      sortComparator: (v1, v2) => {
        const user1 = v1.find(
          (item) => item.historyAction === 'mailing-rule:create',
        )?.user?.user;
        const user2 = v2.find(
          (item) => item.historyAction === 'mailing-rule:create',
        )?.user?.user;
        return user1.localeCompare(user2);
      },
    },
  ];

  return (
    <Box>
      <Grid container direction="column">
        <Grid
          item
          container
          xs={12}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          className={classes.header}
        >
          <Grid item>
            <Typography data-cy="mailing-rules__label--title" variant="h4">
              Listagem de Réguas de Mailing
            </Typography>
          </Grid>
          <Grid item>
            {permissions['mailing-rule:create'] && (
              <Button
                data-cy="mailing-rules__button--create-mailing-rules"
                variant="contained"
                color="primary"
                onClick={handleCreateMailingRule}
              >
                {navigationLoading === REQUEST_LIFE_CICLE.PENDING ? (
                  <CircularProgress color="secondary" size={20} />
                ) : (
                  'Criar nova Régua de Mailing'
                )}
              </Button>
            )}
          </Grid>
        </Grid>
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
        >
          {!permissions['mailing-rule:list'] ? (
            <Typography>
              Sem permissão para visualizar a listagem de Réguas de Mailing.
            </Typography>
          ) : (
            <Grid
              data-cy="mailing-rule-list__datagrid--mailing-rule-list"
              item
              xs={12}
              className={classes.gridWrapper}
            >
              <PaginatedDataGrid
                columns={columns}
                rows={list}
                loading={loading}
                paginationMode="client"
                paginateAction={listMailingRulesAction}
                rowCount={count}
                columnBuffer={columns.length}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default MailingRuleList;
